import React, { useState } from "react";
import { DraggableTool } from "./DraggableTool";
import {
  PencilLineIcon,
  EqualIcon,
  TextIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  MessageSquareIcon,
  LinkIcon,
  ClockIcon,
} from "lucide-react";
import { AnimatePresence, motion, Variants } from "motion/react";
import OpenAIIcon from "./icons/OpenAIIcon";
import { cn } from "@/lib/utils";
import AnthropicIcon from "./icons/AnthropicIcon";
import MetaIcon from "./icons/MetaIcon";

const TOOL_GROUPS = {
  General: [
    {
      type: "customInput",
      label: "Input",
      icon: <PencilLineIcon size={18} strokeWidth={1.5} />,
    },
    {
      type: "customOutput",
      label: "Output",
      icon: <EqualIcon size={18} strokeWidth={1.5} />,
    },
    {
      type: "text",
      label: "Text",
      icon: <TextIcon size={18} strokeWidth={1.5} />,
    },
  ],
  LLMs: [
    {
      type: "openai",
      label: "OpenAI",
      icon: <OpenAIIcon />,
    },
    {
      type: "anthropic",
      label: "Anthropic",
      icon: <AnthropicIcon />,
    },
    {
      type: "llama",
      label: "Llama",
      icon: <MetaIcon />,
    },
  ],
  Others: [
    {
      type: "chat_memory",
      label: "Chat Memory",
      icon: <MessageSquareIcon size={18} />,
    },
    {
      type: "url_loader",
      label: "URL Loader",
      icon: <LinkIcon size={18} />,
    },
    {
      type: "time",
      label: "Time",
      icon: <ClockIcon size={18} />,
    },
  ],
};

export const PipelineToolbar: React.FC<{
  show: boolean;
  setShow: (show: boolean) => void;
}> = ({ show, setShow }) => {
  const container = {
    hidden: { width: 0 },
    visible: {
      width: "auto",
      transition: {
        duration: 0.15,
        delayChildren: 0.15,
      },
    },
    exit: { width: 0 },
  };

  const child = {
    hidden: { opacity: 0 },
    visible: { opacity: 1 },
    exit: { opacity: 0 },
  };

  return (
    <AnimatePresence>
      {show ? (
        <motion.div
          className="flex flex-col h-full gap-2"
          exit="exit"
          animate="visible"
          initial="hidden"
          variants={container}
        >
          <AnimatePresence>
            <motion.div variants={child} className="flex flex-col h-full gap-4">
              <motion.h1 className="flex items-center gap-2 text-2xl font-light text-primary-foreground">
                Tools
              </motion.h1>
              {Object.entries(TOOL_GROUPS).map(([groupName, tools]) => (
                <Collapsible key={groupName} name={groupName}>
                  {tools.map((tool) => (
                    <DraggableTool key={tool.label} type={tool.type}>
                      <div
                        className={cn(
                          "cursor-grab flex items-center justify-center overflow-hidden text-xs flex-col gap-0.5 text-foreground p-2 bg-background",
                          tool.type,
                        )}
                      >
                        {tool.icon}
                        <span>{tool.label}</span>
                      </div>
                    </DraggableTool>
                  ))}
                </Collapsible>
              ))}
            </motion.div>
          </AnimatePresence>
        </motion.div>
      ) : (
        <button
          onClick={() => setShow(true)}
          className="flex flex-col items-center justify-center h-full text-primary-foreground"
        >
          <span className="sr-only">show side panel</span>
          <ChevronRightIcon size={18} strokeWidth={2} />
        </button>
      )}
    </AnimatePresence>
  );
};

export const Collapsible = ({
  children,
  name,
}: {
  children: React.ReactNode;
  name: string;
}) => {
  const [isOpen, setIsOpen] = useState(true);
  const containerVariants: Variants = {
    hidden: { height: 0, opacity: 0 },
    visible: {
      height: "auto",
      opacity: 1,
      transition: {
        duration: 0.3,
        delayChildren: 0.2,
        staggerChildren: 0.1,
      },
    },
    exit: { opacity: 0, height: 0 },
  };

  const childVariants: Variants = {
    hidden: { opacity: 0, y: -20 },
    visible: {
      opacity: 1,
      y: 0,
    },
    exit: { opacity: 0, y: -20 },
  };

  return (
    <motion.div
      className="w-[255px] rounded-md space-y-2"
      transition={{ duration: 0.3 }}
      onClick={() => setIsOpen(!isOpen)}
    >
      <motion.div className="flex items-center justify-between w-full text-primary-foreground">
        <motion.span className="font-medium text-md">{name}</motion.span>
        <ChevronDownIcon
          size={18}
          className={cn(!isOpen ? "-rotate-90" : "")}
        />
      </motion.div>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            className="grid w-full grid-cols-4 gap-2"
          >
            {React.Children.map(children, (child) => (
              <motion.div
                variants={childVariants}
                key={child?.toString()}
                className="col-span-2"
              >
                {child}
              </motion.div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};
